import { BxfDialog, BxfIcon, BxfIconType } from '@blk/bxf-react-components';

import styled from '@emotion/styled';
import {
    activeContentPages,
    ChatContentConstants,
    RESET_WARNING_MESSAGE,
    ResetDialogConstants,
} from '../constants';
import { useChatBlkStore, useHandleBxfEvent } from '../hooks';
import { useChatBlkContext } from '../providers';
import { ChatBlkQueryService } from '../services';
import { telemetryFunctionMap } from '../types';
import { uuidv4 } from '../utils';

const { CAUTION_ICON, DIALOG_HEADER, DATA_CY_RESET_DIALOG } =
    ResetDialogConstants;

const ResetDialogContent = styled.div`
    display: flex;
`;

const ResetCautionIcon = styled(BxfIcon)`
    padding-top: 4px; // $space-xxxs
    padding-right: 12px; // $space-xs
`;

/**
 * Handles the reset of the chat by performing the following actions:
 * - Dispatches an action to clear the chat queries.
 * - Cancels any ongoing prompt query in the chat service.
 * - Tracks the new chat icon click event with telemetry, including the previous session ID.
 * - Updates the session ID in both the chat service and telemetry.
 * - Resets the prompt box value and focuses on it.
 * - Enables the prompt box if it was disabled.
 * - Closes the reset dialog.
 *
 * @returns {JSX.Element}
 */
const ResetDialog = (): JSX.Element => {
    const chatService = ChatBlkQueryService.getInstance();
    const {
        clearAllQueries,
        setPaddingHeight,
        setActiveContent,
        isResetDialogOpen,
        setIsResetDialogOpen,
    } = useChatBlkStore();
    const { promptBoxRef } = useChatBlkContext();
    const handleNewChatSession = useHandleBxfEvent(async () => {
        chatService.cancelQuery();
        clearAllQueries();

        // Since we are starting a New Chat, the Session ID should also be updated.
        chatService.sessionId = uuidv4();
        await promptBoxRef.current?.calculateTextBoxHeight();
        await promptBoxRef.current?.setValue('');
        setIsResetDialogOpen(false);
        setActiveContent(activeContentPages.CHAT);
        setPaddingHeight(0);
    }, telemetryFunctionMap.CHATBLK_MFE_FUNCTION_RESET_CLICKED);
    const handleCloseDialog = () => {
        setIsResetDialogOpen(false);
    };

    return (
        <BxfDialog
            slot={ChatContentConstants.OVERLAY_SLOT}
            closeAllDialogsOnEscape
            header={DIALOG_HEADER}
            isOpen={isResetDialogOpen}
            contentTopDistance='15%'
            data-cy={DATA_CY_RESET_DIALOG}
            dataLinkEvent={'chatblk | menu | reset'}
            onPrimaryButtonClicked={handleNewChatSession}
            onSecondaryButtonClicked={handleCloseDialog}
        >
            <ResetDialogContent>
                <ResetCautionIcon type={CAUTION_ICON as BxfIconType} />
                {RESET_WARNING_MESSAGE}
            </ResetDialogContent>
        </BxfDialog>
    );
};

export default ResetDialog;
